/**
 * StoryPanel — Histories shell: data loading (useStoryMap), the Now bar, the
 * theme map (MapLens — the only lens; the braid "timeline" was removed), the
 * moment drawer (quote + echoes), and the moment-opening callback.
 */
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useStoryMap } from "./useStoryMap";
import { MapLens } from "./MapLens";
import { EGYEB_TONE, VALENCE_TOKENS } from "./storyPalette";
import { formatShortDate } from "./braidGeometry";
import type { StoryMapOk, StoryTheme } from "./types";
import "./StoryPanel.css";

export interface StoryPanelProps {
  patientId: string;
  currentSessionId?: string;
  onOpenSession: (
    sessionId: string,
    opts?: { highlightMs?: [number, number] },
  ) => void;
}

/**
 * Resolves a moment id to its session + highlight range and forwards it to
 * onOpenSession. Exported so the mapping is unit-testable; StoryPanel passes
 * the resulting callback down to the lenses.
 */
export function resolveOpenMoment(
  data: StoryMapOk,
  onOpenSession: StoryPanelProps["onOpenSession"],
): (momentId: number) => void {
  return (momentId: number) => {
    const moment = data.moments.find((m) => m.id === momentId);
    if (!moment) {
      throw new Error(`[StoryPanel] unknown moment id: ${momentId}`);
    }
    onOpenSession(moment.session_id, {
      highlightMs: [moment.start_ms, moment.end_ms],
    });
  };
}

export function StoryPanel({
  patientId,
  currentSessionId,
  onOpenSession,
}: StoryPanelProps) {
  const { t, i18n } = useTranslation();
  const { data, loading, error } = useStoryMap(patientId);
  const [selectedMomentId, setSelectedMomentId] = useState<number | null>(null);

  if (error) {
    return (
      <div className="sty-error" role="alert">
        <strong>{t("stories.loadError")}</strong>
        <span className="sty-error-detail">{error}</span>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="sty-loading" aria-busy="true" />;
  }

  if (data.status === "needs_more_sessions") {
    return (
      <div className="sty-coldstart">
        <div className="sty-kicker">{t("stories.title")}</div>
        <p>
          {t("stories.coldStart", {
            sessions: data.session_count,
            minSessions: data.min_sessions,
            moments: data.moment_count,
            minMoments: data.min_moments,
          })}
        </p>
      </div>
    );
  }

  const sessions = data.sessions;
  const latest =
    (currentSessionId &&
      sessions.find((s) => s.id === currentSessionId)) ||
    sessions[sessions.length - 1];

  const realThemes = data.themes.filter((th) => th.id >= 0);
  const chipThemes = realThemes.filter(
    (th) => (th.per_session[latest.id] ?? 0) > 0,
  );

  // Now-bar fact: prefer the top fading theme, else the top rising one.
  const factTheme: StoryTheme | undefined =
    realThemes.find((th) => th.trend === "fading") ??
    realThemes.find((th) => th.trend === "rising");
  let fact: string | null = null;
  if (factTheme) {
    const firstSession = sessions.find(
      (s) => factTheme.per_session[s.id] != null,
    );
    if (firstSession) {
      fact = t("stories.nowFact", {
        theme: factTheme.label,
        early: factTheme.per_session[firstSession.id],
        now: factTheme.per_session[latest.id] ?? 0,
      });
    }
  }

  const onOpenMoment = resolveOpenMoment(data, onOpenSession);

  const themeById = (id: number): StoryTheme => {
    const th = data.themes.find((tt) => tt.id === id);
    if (!th) throw new Error(`[StoryPanel] unknown theme id: ${id}`);
    return th;
  };
  const themeLabel = (th: StoryTheme): string =>
    th.id === -1 ? t("stories.egyeb") : th.label ?? "";
  const themeTone = (th: StoryTheme): string =>
    th.id === -1 ? EGYEB_TONE : VALENCE_TOKENS[th.valence].tone;

  const handleSelectMoment = (momentId: number) => {
    setSelectedMomentId(momentId);
  };

  // ── Drawer content ──
  let drawer: ReactNode = null;
  if (selectedMomentId != null) {
    const moment = data.moments.find((m) => m.id === selectedMomentId);
    if (!moment) {
      throw new Error(
        `[StoryPanel] unknown selected moment id: ${selectedMomentId}`,
      );
    }
    const th = themeById(moment.theme_id);
    const sess = sessions.find((s) => s.id === moment.session_id);
    if (!sess) {
      throw new Error(
        `[StoryPanel] unknown session id: ${moment.session_id}`,
      );
    }
    drawer = (
      <div className="sty-drawer" role="region" aria-label={t("stories.title")}>
        <div className="sty-dhead">
          <span className="sty-dtheme" style={{ color: themeTone(th) }}>
            {themeLabel(th)}
          </span>
          <span className="sty-ddate">
            {formatShortDate(sess.date, i18n.language)}
          </span>
        </div>
        <blockquote className="sty-dquote">
          <button
            type="button"
            className="sty-quote-btn"
            title={t("stories.openTranscript")}
            onClick={() => onOpenMoment(moment.id)}
          >
            „{moment.text}{moment.text.length >= 300 ? "…" : ""}”
          </button>
        </blockquote>
        {moment.echoes.length > 0 && (
          <>
            <div className="sty-echohead">{t("stories.echoes")}</div>
            {moment.echoes.map((e) => {
              const em = data.moments.find((mm) => mm.id === e.moment_id);
              if (!em) {
                throw new Error(
                  `[StoryPanel] unknown echo moment id: ${e.moment_id}`,
                );
              }
              const eth = themeById(em.theme_id);
              const esess = sessions.find((s) => s.id === em.session_id);
              if (!esess) {
                throw new Error(
                  `[StoryPanel] unknown session id: ${em.session_id}`,
                );
              }
              return (
                <button
                  key={e.moment_id}
                  type="button"
                  className="sty-echoitem"
                  onClick={() => onOpenMoment(em.id)}
                >
                  <span className="sty-echo-open">
                    {t("stories.openTranscript")}
                  </span>
                  <span className="sty-echo-meta">
                    {formatShortDate(esess.date, i18n.language)} ·{" "}
                    {themeLabel(eth)}
                  </span>
                  <q>{em.text.slice(0, 150)}{em.text.length > 150 ? "…" : ""}</q>
                </button>
              );
            })}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="sty-panel">
      <div className="sty-kicker">{t("stories.title")}</div>

      <div className="sty-nowbar">
        <b>
          {t("stories.nowLast")} · {latest.date}:
        </b>
        {chipThemes.map((th) => (
          <span
            key={th.id}
            className="sty-chip"
            style={{
              color: VALENCE_TOKENS[th.valence].tone,
              background: VALENCE_TOKENS[th.valence].ghost,
            }}
          >
            {th.label} · {th.per_session[latest.id]}
          </span>
        ))}
        {fact && <span className="sty-nowfact">{fact}</span>}
      </div>

      <div data-testid="map-lens">
        <MapLens
          data={data}
          selectedMomentId={selectedMomentId}
          onSelectMoment={handleSelectMoment}
        />
      </div>

      {drawer}

      {data.computed_over.capped && (
        <div className="sty-capped">
          {t("stories.capped", { sessions: data.computed_over.sessions })}
        </div>
      )}
    </div>
  );
}
